import type { JSX } from 'react';
import styles from './Accrochage.module.css';

/**
 * - `accroche` : le mur tel qu'on le prépare (connexion, invitation…) ;
 * - `manquant` : l'œuvre du milieu a été décrochée, il ne reste que sa trace
 *   sur le mur (page introuvable) ;
 * - `decroche` : tout le mur est décroché (archive d'une galerie résiliée).
 */
export type EtatAccrochage = 'accroche' | 'manquant' | 'decroche';

export interface AccrochageProps {
  etat?: EtatAccrochage;
  className?: string;
}

interface Cadre {
  readonly x: number;
  readonly y: number;
  readonly l: number;
  readonly h: number;
  /** Largeur du passe-partout autour de l'œuvre. */
  readonly marge: number;
  readonly teinte: 'a' | 'b' | 'c';
}

/** Rail de cimaise, axe d'accrochage, cotes et sol, en unités du `viewBox`. */
const RAIL = 36;
const AXE = 226;
const COTE = 380;
const SOL = 418;

/** Trois formats d'un même accrochage, centrés sur l'axe (portrait, paysage, carré). */
const CADRES: readonly Cadre[] = [
  { x: 36, y: 128, l: 150, h: 196, marge: 22, teinte: 'a' },
  { x: 214, y: 156, l: 210, h: 140, marge: 18, teinte: 'b' },
  { x: 452, y: 176, l: 84, h: 100, marge: 14, teinte: 'c' },
];

/**
 * Plan d'accrochage d'un mur de galerie, dessiné aux jetons Halo : rail de
 * cimaise, fils, cadres et passe-partout, cartels, axe d'accrochage et cotes.
 * Un objet du métier (la feuille qu'on trace avant d'accrocher une
 * exposition), pas une illustration : il porte l'identité des écrans d'entrée
 * et des pages d'état. Purement décoratif pour les technologies d'assistance.
 */
export function Accrochage({ etat = 'accroche', className }: AccrochageProps): JSX.Element {
  const classes = [styles.plan, className].filter(Boolean).join(' ');

  return (
    <svg
      className={classes}
      viewBox="0 0 560 440"
      aria-hidden="true"
      focusable="false"
      data-etat={etat}
    >
      <line className={styles.rail} x1="0" y1={RAIL} x2="560" y2={RAIL} />
      <line className={styles.axe} x1="8" y1={AXE} x2="552" y2={AXE} />
      <line className={styles.sol} x1="0" y1={SOL} x2="560" y2={SOL} />

      {CADRES.map((cadre, index) => {
        const centre = cadre.x + cadre.l / 2;
        const decroche = etat === 'decroche' || (etat === 'manquant' && index === 1);
        return (
          <g
            key={cadre.teinte}
            className={styles.groupe}
            style={{ animationDelay: `${120 + index * 90}ms` }}
          >
            <circle className={styles.crochet} cx={centre} cy={RAIL} r="3" />
            <Cote debut={cadre.x} fin={cadre.x + cadre.l} />

            {decroche ? (
              <>
                {/* Ce que laisse une œuvre décrochée : le clou, et la trace
                    de son cadre sur le mur. */}
                <rect
                  className={styles.trace}
                  x={cadre.x}
                  y={cadre.y}
                  width={cadre.l}
                  height={cadre.h}
                  rx="2"
                />
                <circle className={styles.clou} cx={centre} cy={cadre.y - 14} r="2.5" />
              </>
            ) : (
              <>
                <polyline
                  className={styles.fil}
                  points={`${cadre.x + cadre.l * 0.18},${cadre.y} ${centre},${RAIL} ${cadre.x + cadre.l * 0.82},${cadre.y}`}
                />
                <rect
                  className={styles.cadre}
                  x={cadre.x}
                  y={cadre.y}
                  width={cadre.l}
                  height={cadre.h}
                  rx="2"
                />
                <rect
                  className={styles.passePartout}
                  x={cadre.x + 6}
                  y={cadre.y + 6}
                  width={cadre.l - 12}
                  height={cadre.h - 12}
                />
                <rect
                  className={styles[`oeuvre-${cadre.teinte}`]}
                  x={cadre.x + cadre.marge}
                  y={cadre.y + cadre.marge}
                  width={cadre.l - cadre.marge * 2}
                  height={cadre.h - cadre.marge * 2}
                />
                <Cartel x={cadre.x + cadre.l - 34} y={cadre.y + cadre.h + 14} />
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Le cartel posé à droite, sous l'œuvre : deux lignes, l'artiste et le titre. */
function Cartel({ x, y }: { x: number; y: number }): JSX.Element {
  return (
    <g className={styles.cartel}>
      <rect x={x} y={y} width="34" height="20" rx="1.5" />
      <line x1={x + 5} y1={y + 7} x2={x + 26} y2={y + 7} />
      <line x1={x + 5} y1={y + 13} x2={x + 20} y2={y + 13} />
    </g>
  );
}

/** Ligne de cote sous un cadre, avec ses deux traits de rappel. */
function Cote({ debut, fin }: { debut: number; fin: number }): JSX.Element {
  return (
    <g className={styles.cote}>
      <line x1={debut} y1={COTE} x2={fin} y2={COTE} />
      <line x1={debut} y1={COTE - 6} x2={debut} y2={COTE + 6} />
      <line x1={fin} y1={COTE - 6} x2={fin} y2={COTE + 6} />
    </g>
  );
}
